/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */
/* eslint-disable max-len */
/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Popover, Box, Text, useMantineColorScheme, RangeSlider, NumberInput,
} from '@mantine/core';
import { X } from 'tabler-icons-react';
import { renderCompactNumber, assetPrefix } from '../../../utilities/general';
import NewRangePopoverContent from './NewRangePopoverContent';

const CustomRangeSelector = (props) => {
  const {
    setValue, value, defaultLabel, title, popoverOpenRef, originalData, parentKey, childKey, currency,
  } = props;

  const maxRangeTotal = originalData?.reduce((max, item) => {
    const value = childKey ? item?.[parentKey]?.[childKey] : item?.[parentKey];

    if (value == null) return max;

    const currentMax = (max === null || value > max) ? value : max;

    if (currentMax == null) return null;

    return !Number.isInteger(currentMax) ? Math.ceil(currentMax) + 1 : currentMax;
  }, null);

  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);
  const [tempMin, setTempMin] = useState(0);
  const [tempMax, setTempMax] = useState(maxRangeTotal || 0);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [range, setRange] = useState('');
  const [minError, setMinError] = useState('');
  const [maxError, setMaxError] = useState('');
  const [hasValidated, setHasValidated] = useState(false);

  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    if (!value || !Array.isArray(value) || value.length !== 2) {
      setMin(null);
      setMax(null);
      setTempMin(0);
      setTempMax(maxRangeTotal || 0);
      setRange('');
      setMinError('');
      setMaxError('');
      setHasValidated(false);
    } else {
      setMin(value[0]);
      setMax(value[1]);
      setTempMin(value[0]);
      setTempMax(value[1]);
      setRange(`${renderCompactNumber(value[0], currency)} - ${renderCompactNumber(value[1], currency)}`);
    }
  }, [value, maxRangeTotal]);

  useEffect(() => {
    if (popoverOpened) {
      setTempMin(min !== null ? min : 0);
      setTempMax(max !== null ? max : maxRangeTotal || 0);
      setMinError('');
      setMaxError('');
    }
  }, [popoverOpened, min, max, maxRangeTotal]);

  useEffect(() => {
    if (popoverOpenRef) {
      popoverOpenRef.current = popoverOpened;
    }
  }, [popoverOpened, popoverOpenRef]);

  const validateRange = (minVal, maxVal, showErrors = false) => {
    let isValid = true;
    const parsedMin = parseFloat(minVal);
    const parsedMax = parseFloat(maxVal);

    if (minVal === '' || isNaN(parsedMin)) {
      if (showErrors) setMinError('Minimum is required');
      isValid = false;
    } else {
      setMinError('');
    }

    if (maxVal === '' || isNaN(parsedMax)) {
      if (showErrors) setMaxError('Maximum is required');
      isValid = false;
    } else {
      setMaxError('');
    }

    if (isValid && parsedMin > parsedMax) {
      if (showErrors) {
        setMinError('Minimum cannot be greater than maximum');
        setMaxError('Maximum cannot be less than minimum');
      }
      isValid = false;
    }

    return isValid;
  };

  const handleConfirm = () => {
    const isValid = validateRange(tempMin, tempMax, true);
    setHasValidated(true);
    if (!isValid) return;

    const parsedMin = parseFloat(tempMin);
    const parsedMax = parseFloat(tempMax);

    setMin(parsedMin);
    setMax(parsedMax);
    setRange(`${renderCompactNumber(parsedMin)} - ${renderCompactNumber(parsedMax)}`);
    setValue([parsedMin, parsedMax]);
    setPopoverOpened(false);
  };

  const handleClear = () => {
    setMin(null);
    setMax(null);
    setTempMin(0);
    setTempMax(maxRangeTotal || 0);
    setRange('');
    setValue(null);
    setMinError('');
    setMaxError('');
    setHasValidated(false);
    setPopoverOpened(false);
  };

  const handleClosePopover = () => {
    setPopoverOpened(false);
    setTempMin(min !== null ? min : 0);
    setTempMax(max !== null ? max : maxRangeTotal || 0);
    setMinError('');
    setMaxError('');
  };

  const displayValueOfSelectedRange = useMemo(() => {
    if (!range) return null;
    return <div className="text-[#33B1FF]">{range}</div>;
  }, [range]);

  return (
    <div className="w-[100%]">
      <Box className={`flex flex-col gap-3 border-b ${colorScheme === 'dark' && 'border-neutral-600'} pb-5`}>
        <div className="flex justify-between font-bold">
          <div>{title}</div>
          <div>{displayValueOfSelectedRange}</div>
        </div>
        <Popover
          opened={popoverOpened}
          onChange={setPopoverOpened}
          position="bottom-start"
          width={300}
          shadow="md"
          withinPortal
          styles={{
            dropdown: {
              border: `1px solid ${colorScheme === 'dark' ? '#737373' : '#E8E8E8'}`,
            },
          }}
        >
          <Popover.Target>
            <Box
              onClick={() => setPopoverOpened((o) => !o)}
              className={`
                flex items-center justify-between gap-1 
                border rounded-md h-12 px-1.5 py-1 cursor-pointer 
                ${colorScheme === 'dark' ? 'border-[#737373] bg-[#1E1E1E]' : 'border-[#E8E8E8] bg-white'}
              `}
            >
              <Text>
                {range ? (
                  <div className={`inline-flex items-center px-2 ${colorScheme === 'dark' ? 'bg-[#1E293B] text-[#F4F4F5]' : 'bg-[#EEF4FF] text-[#2E2E2E]'} text-md font-medium rounded-2xl whitespace-nowrap`}>
                    {range}
                  </div>
                ) : (
                  <div className="inline-flex items-center px-2 text-[#898989] text-md font-medium whitespace-nowrap">
                    {defaultLabel?.length > 13 ? `${defaultLabel?.slice(0, 13)}...` : defaultLabel || ''}
                  </div>
                )}
              </Text>
              <div className="flex gap-2 ml-1 items-center">
                <img className="w-3 h-3" src={`${assetPrefix}/images/DownArrow.svg`} alt="downarrow" />
                <X
                  size={16}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  className="mr-1 text-gray-500 cursor-pointer"
                />
              </div>
            </Box>
          </Popover.Target>

          <Popover.Dropdown className={`h-auto w-[264px] px-[29px] pt-4 pb-4 ${colorScheme === 'dark' ? 'bg-[#1a1b1e]' : ''} rounded-md`}>
            <Box
              className="cursor-pointer absolute top-2 right-2"
              onClick={handleClosePopover}
            >
              <X size={18} color="#898989" />
            </Box>
            <Box className="mb-10" w={230}>
              <RangeSlider
                mt="xl"
                styles={{
                  // Track styling (the background rail)
                  track: {
                    height: 6,
                    backgroundColor: colorScheme === 'dark' ? '#373A40' : '#f1f3f5',
                  },

                  // Filled bar styling (active portion of the slider)
                  bar: {
                    backgroundColor: '#33B1FF',
                    height: 6,
                  },

                  // Thumb styling (the draggable circles)
                  thumb: {
                    backgroundColor: '#33B1FF',
                    border: 'none',
                    width: 16,
                    height: 16,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  },

                  // Mark styling
                  mark: {
                    display: 'none', // Hide regular marks
                  },

                  // Filled mark styling
                  markFilled: {
                    border: 'none',
                    backgroundColor: 'transparent',
                  },

                  // Label styling
                  markLabel: {
                    fontSize: 14,
                    color: colorScheme === 'dark' ? '#fff' : '#000',
                    '&[data-value="0"]': {
                      transform: 'translateX(0)',
                      marginLeft: 0,
                    },
                    '&[data-value="100"]': {
                      transform: 'translateX(-100%)',
                      marginRight: 0,
                      color: '#33B1FF',
                      fontWeight: 500,
                    },
                  },
                  // Mark wrapper styling
                  markWrapper: {
                    position: 'absolute',
                    top: -1,
                    zIndex: 2,
                    marginTop: 10,
                    color: colorScheme === 'dark' ? '#fff' : '#000',
                    '&:first-of-type': {
                      left: '0%',
                    },
                    '&:last-of-type': {
                      left: '99.066765%',
                    },
                  },
                  // Label container
                  label: {
                    backgroundColor: '#33B1FF',
                    color: '#fff',
                    fontSize: 12,
                    height: 'auto',
                    padding: '2px 6px',
                    borderRadius: 4,
                    top: -32,
                    fontWeight: 500,
                  },
                  // Root container
                  root: {
                    paddingTop: 15,
                    paddingBottom: 15,
                  },
                }}
                minRange={0}
                min={0}
                max={maxRangeTotal}
                // step={0.5}
                value={[tempMin, tempMax]}
                marks={[
                  { value: 0, label: renderCompactNumber(0, currency) },
                  { value: 100, label: renderCompactNumber(maxRangeTotal, currency) },
                ]}
                onChange={([minVal, maxVal]) => {
                  setTempMin(minVal);
                  setTempMax(maxVal);
                  if (hasValidated) validateRange(minVal, maxVal);
                }}
              />
            </Box>
            <NewRangePopoverContent
              onConfirm={handleConfirm}
              onClear={handleClear}
            // onClose={handleClosePopover}
            >
              <NumberInput
                placeholder="Minimum"
                value={tempMin}
                onChange={(val) => {
                  setTempMin(val || 0);
                  if (hasValidated) validateRange(val, tempMax);
                }}
                parser={(val) => val
                  // .replace(/[^0-9.]/g, '')
                  // .replace(/^\.*/, '')
                  // .replace(/(\..*)\./g, '$1')}
                  .replace(/[^0-9.-]/g, '')
                  .replace(/(?!^)-/g, '')
                  .replace(/(\..*)\./g, '$1')}
                classNames={{
                  input: `${colorScheme === 'dark' ? 'bg-[#1a1b1e] border-[#737373] text-white' : ''}`,
                }}
                error={minError}
                precision={2}
                step={0.005}
                min={0}
                hideControls
              />
              <NumberInput
                placeholder="Maximum"
                value={tempMax}
                onChange={(val) => {
                  setTempMax(val || 0);
                  if (hasValidated) validateRange(tempMin, val);
                }}
                classNames={{
                  input: `${colorScheme === 'dark' ? 'bg-[#1a1b1e] border-[#737373] text-white' : ''}`,
                }}
                error={maxError}
                precision={2}
                parser={(val) => val
                  // .replace(/[^0-9.]/g, '')
                  // .replace(/^\.*/, '')
                  // .replace(/(\..*)\./g, '$1')}
                  .replace(/[^0-9.-]/g, '')
                  .replace(/(?!^)-/g, '')
                  .replace(/(\..*)\./g, '$1')}
                // step={0.1}
                max={maxRangeTotal}
                step={0.005}
                hideControls
              />
            </NewRangePopoverContent>
          </Popover.Dropdown>
        </Popover>
      </Box>
    </div>
  );
};

export default CustomRangeSelector;
